/**
 * The arrow layer. UI_UX section 7: targeting draws a curved arrow from the
 * source to the cursor, attackers draw a red arrow to the opponent avatar, and
 * blockers draw a shield arrow to the attacker they block.
 *
 * Arrows are one absolutely positioned SVG over the whole board, so they can
 * cross row boundaries without being clipped. Endpoints are looked up from the
 * DOM by the data attributes CardFrame and PlayerBar already write, which keeps
 * layout knowledge out of the components that own the cards.
 */

import { useEffect, useRef, useState, type RefObject } from 'react';
import styles from './Battlefield.module.css';

export type ArrowKind = 'target' | 'attack' | 'block';

export interface ArrowSpec {
  id: string;
  kind: ArrowKind;
  /** Instance id, or `player:<id>` for a player avatar. */
  from: string;
  /** Absent for the targeting arrow, which follows the cursor. */
  to?: string;
}

const COLOR: Record<ArrowKind, string> = {
  target: 'var(--accent)',
  attack: 'var(--danger)',
  block: 'var(--xrp)',
};

interface Point {
  x: number;
  y: number;
}

export function ArrowLayer({
  arrows,
  containerRef,
  followCursor,
}: {
  arrows: ArrowSpec[];
  containerRef: RefObject<HTMLElement>;
  /** True while a targeting arrow should chase the pointer. */
  followCursor: boolean;
}) {
  const [cursor, setCursor] = useState<Point | null>(null);
  const [, force] = useState(0);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    if (!followCursor) {
      setCursor(null);
      return;
    }
    const onMove = (e: PointerEvent) => {
      const box = containerRef.current?.getBoundingClientRect();
      if (!box) return;
      setCursor({ x: e.clientX - box.left, y: e.clientY - box.top });
    };
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, [followCursor, containerRef]);

  // Endpoints are read from the DOM, so a scroll or resize moves the arrows.
  useEffect(() => {
    const onChange = () => {
      if (frame.current !== null) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        force((n) => n + 1);
      });
    };
    window.addEventListener('scroll', onChange, true);
    window.addEventListener('resize', onChange);
    onChange();
    return () => {
      window.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
  }, [arrows]);

  const container = containerRef.current;
  if (!container || arrows.length === 0) return null;
  const box = container.getBoundingClientRect();

  const drawn = arrows
    .map((arrow) => {
      const from = anchorOf(container, box, arrow.from);
      if (!from) return null;
      const to = arrow.to ? anchorOf(container, box, arrow.to) : cursor;
      if (!to) return null;
      return { arrow, from, to };
    })
    .filter((v): v is { arrow: ArrowSpec; from: Point; to: Point } => v !== null);

  if (drawn.length === 0) return null;

  return (
    <svg className={styles.arrowLayer} width={box.width} height={box.height} aria-hidden="true">
      <defs>
        {(['target', 'attack', 'block'] as ArrowKind[]).map((kind) => (
          <marker
            key={kind}
            id={`arrowhead-${kind}`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="5"
            markerHeight="5"
            orient="auto-start-reverse"
          >
            <path d="M0 0 L10 5 L0 10 z" fill={COLOR[kind]} />
          </marker>
        ))}
      </defs>
      {drawn.map(({ arrow, from, to }) => (
        <g key={arrow.id}>
          <path
            d={bezier(from, to)}
            fill="none"
            stroke={COLOR[arrow.kind]}
            strokeWidth={arrow.kind === 'block' ? 2.5 : 3}
            strokeLinecap="round"
            strokeDasharray={arrow.kind === 'block' ? '7 5' : undefined}
            opacity={0.92}
            markerEnd={`url(#arrowhead-${arrow.kind})`}
          />
          <circle cx={from.x} cy={from.y} r={4} fill={COLOR[arrow.kind]} />
        </g>
      ))}
    </svg>
  );
}

/**
 * A vertical-ish cubic bezier. Control points are pushed along the dominant
 * axis so an arrow between two rows bows outward instead of cutting through
 * the cards between them.
 */
function bezier(from: Point, to: Point): string {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const bow = Math.min(120, Math.max(40, Math.abs(dy) * 0.55 + Math.abs(dx) * 0.12));
  const sign = dy === 0 ? 1 : Math.sign(dy);
  const c1 = { x: from.x + dx * 0.1, y: from.y + sign * bow };
  const c2 = { x: to.x - dx * 0.1, y: to.y - sign * bow };
  return `M${from.x} ${from.y} C${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${to.x} ${to.y}`;
}

/**
 * Centre of the element an arrow endpoint names, relative to the container.
 *
 * A player endpoint aims at their Integrity badge, which is what an attack
 * threatens and which sits inboard rather than in a corner. The avatar is the
 * fallback, so an arrow still lands if a bar ever renders without the badge.
 */
function anchorOf(container: HTMLElement, box: DOMRect, ref: string): Point | null {
  const selectors = ref.startsWith('player:')
    ? [
        `[data-integrity-anchor="${cssEscape(ref.slice('player:'.length))}"]`,
        `[data-player-avatar="${cssEscape(ref.slice('player:'.length))}"]`,
      ]
    : [`[data-instance-id="${cssEscape(ref)}"]`];

  for (const selector of selectors) {
    const el = container.querySelector(selector);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2 - box.left, y: r.top + r.height / 2 - box.top };
  }
  return null;
}

function cssEscape(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}
